import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Pagination,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';

interface PaginationControlProps {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  limitOptions?: number[];
  showItemCount?: boolean;
}

const PaginationControl: React.FC<PaginationControlProps> = ({
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  limitOptions = [10, 25, 50, 100],
  showItemCount = true,
}) => {
  const { t } = useTranslation();
  const totalPages = Math.ceil(total / limit);
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  if (total === 0) return null;

  const handleLimitChange = (e: SelectChangeEvent<number>) => {
    onLimitChange?.(Number(e.target.value));
    onPageChange(1);
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      gap={2}
      mt={3}
    >
      {showItemCount && (
        <Typography variant="body2" color="text.secondary">
          {t('components.pagination.showingRange', {
            from: startItem,
            to: endItem,
            total,
          })}
        </Typography>
      )}

      <Box display="flex" alignItems="center" gap={2} ml="auto">
        {onLimitChange && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              {t('components.pagination.perPage')}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 72 }}>
              <Select<number>
                value={limit}
                onChange={handleLimitChange}
                variant="outlined"
                sx={{ height: 32, fontSize: '0.875rem' }}
              >
                {limitOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => onPageChange(value)}
            color="primary"
            shape="rounded"
            size="medium"
          />
        )}
      </Box>
    </Box>
  );
};

export default PaginationControl;
